import { getApiUrlSync } from '../company/session';

// Define the UrlFactory class
class UrlFactory {
    /**
     * The API of the signed-in company, read on every call (company/session.js). Without a
     * session: Mary's, `REACT_APP_API_URL || https://service.shelfve.com`, as always.
     */
    get apiUrl() {
        return getApiUrlSync();
    }

    // -------- USER --------
    createSignInUrl() {
        return `${this.apiUrl}/auth/sign-in`;
    }

    /**
     * Password recovery next to sign-in: `step` is 'forgot' | 'verify' | 'reset'. `apiUrl` is the
     * API of the company the email belongs to (forgot-password page), not the session's.
     */
    createPasswordUrl(step, apiUrl = this.apiUrl) {
        return `${apiUrl}/auth/password/${step}`;
    }

    createUserUrl() {
        return `${this.apiUrl}/user`;
    }

    createUpdateUserByIdUrl(id) {
        return `${this.apiUrl}/user/update/${id}`;
    }

    createUserByTokenUrl() {
        return `${this.apiUrl}/user/token`;
    }

    createUserByIdUrl(id) {
        return `${this.apiUrl}/user/${id}`;
    }

    createAllUsersUrl() {
        return `${this.apiUrl}/user/users`;
    }

    createUsersByUserTypeUrl(userType) {
        return `${this.apiUrl}/user/type/${userType}`;
    }

    createPromotorsStatsUrl(channel = 'all', forceRefresh = false, clientId = null) {
        const base = `${this.apiUrl}/user/promotors/stats?channel=${channel}&forceRefresh=${forceRefresh}`;
        return clientId ? `${base}&clientId=${clientId}` : base;
    }

    // -------- NOTIFICATIONS ----------
    createNotificationsUrl() {
        return `${this.apiUrl}/notification`;
    }

    createNotificationByIdUrl(id) {
        return `${this.apiUrl}/notification/${id}`;
    }

    createNotificationStatsUrl() {
        return `${this.apiUrl}/notification/stats`;
    }

    createNotificationUnreadCountUrl() {
        return `${this.apiUrl}/notification/unread-count`;
    }

    createMarkNotificationAsReadUrl(id) {
        return `${this.apiUrl}/notification/${id}/read`;
    }

    createMarkMultipleNotificationsAsReadUrl() {
        return `${this.apiUrl}/notification/read-multiple`;
    }

    createDismissNotificationUrl(id) {
        return `${this.apiUrl}/notification/${id}/dismiss`;
    }

    createDeleteNotificationUrl(id) {
        return `${this.apiUrl}/notification/${id}`;
    }

    // -------- CLIENTS ----------
    createClientUrl() {
        return `${this.apiUrl}/client`;
    }

    createGetClientsUrl() {
        return `${this.apiUrl}/client/clients`;
    }

    createGetClientByIdUrl(id) {
        return `${this.apiUrl}/client/${id}`;
    }

    createUpdateClientByIdUrl(id) {
        return `${this.apiUrl}/client/update/${id}`;
    }

    // --------- STORES ---------
    createStoreUrl() {
        return `${this.apiUrl}/store`;
    }

    createBulkDeactivateStoresUrl() {
        return `${this.apiUrl}/store/bulk-deactivate`;
    }

    createGetStoresUrl() {
        return `${this.apiUrl}/store/stores`;
    }

    createGetStoresCachedUrl(forceRefresh = false) {
        return `${this.apiUrl}/store/stores/cached?forceRefresh=${forceRefresh}`;
    }

    createUpdateAuditorUrl() {
        return `${this.apiUrl}/store/auditor`;
    }

    createStoresByAuditorUrl(auditor) {
        return `${this.apiUrl}/store/auditor/${auditor}`;
    }

    createStoresByClientUrl(client) {
        return `${this.apiUrl}/store/client/${client}`;
    }

    createGetStoreByIdUrl(id) {
        return `${this.apiUrl}/store/${id}`;
    }

    createUpdateStoreByIdUrl(id) {
        return `${this.apiUrl}/store/update/${id}`;
    }

    // -------- STORE CHECKS --------
    createStoreCheckByMonthUrl(storeId, startDate, endDate) {
        if (startDate && endDate) return `${this.apiUrl}/storeCheck/store/${storeId}?startDate=${startDate}&endDate=${endDate}`;
        return `${this.apiUrl}/storeCheck/store/${storeId}`;
    }

    createStoreCheckFilteredUrl(date, userId) {
        return `${this.apiUrl}/storeCheck/filtered?date=${date}&userId=${userId}`;
    }

    createAllStoreChecksUrl(limit = 10000) {
        return `${this.apiUrl}/storeCheck?limit=${limit}&sortBy=createdAt&sortOrder=desc`;
    }

    createStoreCheckById(storeId) {
        return `${this.apiUrl}/storeCheck/${storeId}`;
    }

    storeCheckByClientUrl(clientId, startDate, endDate) {
        if (startDate && endDate) return `${this.apiUrl}/storeCheck/client/${clientId}?startDate=${startDate}&endDate=${endDate}`;
        return `${this.apiUrl}/storeCheck/client/${clientId}`;
    }

    createStoreCheckByUserUrl(userId, startDate, endDate) {
        if (startDate && endDate) return `${this.apiUrl}/storeCheck/user/${userId}?startDate=${startDate}&endDate=${endDate}`;
        return `${this.apiUrl}/storeCheck/user/${userId}`;
    }

    createStoreCheckByContractId(tradeContractId) {
        return `${this.apiUrl}/storeCheck/contract/${tradeContractId}`;
    }

    // ------- DASHBOARD --------
    createDashboardAnalyticsUrl(startDate, endDate, clientId = null) {
        const base =
            startDate && endDate ? `${this.apiUrl}/dashboard/analytics?startDate=${startDate}&endDate=${endDate}` : `${this.apiUrl}/dashboard/analytics`;
        return clientId ? `${base}${startDate && endDate ? '&' : '?'}clientId=${clientId}` : base;
    }

    // ------- CALENDAR --------
    createCalendarUrl() {
        return `${this.apiUrl}/calendar`;
    }

    createOrUpdateCalendarUrl() {
        return `${this.apiUrl}/calendar/update`;
    }

    createCalendarByUserUrl(userId) {
        return `${this.apiUrl}/calendar/user/${userId}`;
    }

    // ------- PERMISSIONS (admin only) --------
    createPermissionsUrl(role) {
        return `${this.apiUrl}/permissions${role ? `/${encodeURIComponent(role)}` : ''}`;
    }

    // ------- PRICE --------
    createPriceUrl() {
        return `${this.apiUrl}/price`;
    }

    createGetPriceByTypeUrl(type, date) {
        if (!date) return `${this.apiUrl}/price/${type}`;
        return `${this.apiUrl}/price/${type}?date=${date}`;
    }

    createGetPricesUrl() {
        return `${this.apiUrl}/price/prices`;
    }

    // Per-piso (MODERNO store) price list that overrides the client's.
    createStorePriceListUrl(storeId) {
        return `${this.apiUrl}/price/store/${storeId}`;
    }

    createStorePriceListsByClientUrl(clientId) {
        return `${this.apiUrl}/price/store-lists/${clientId}`;
    }

    // The list that applies to a store (its own, else its client's), as of `date`.
    createResolveStorePriceUrl(storeId, date) {
        return `${this.apiUrl}/price/resolve/store/${storeId}${date ? `?date=${encodeURIComponent(date)}` : ''}`;
    }

    // ------- PRODUCTS --------
    createProductsUrl(page = 1, limit = 50, category = null, search = null, includeInactive = false) {
        let url = `${this.apiUrl}/products?page=${page}&limit=${limit}`;
        if (category) url += `&category=${category}`;
        if (search) url += `&search=${search}`;
        if (includeInactive) url += `&includeInactive=true`;
        return url;
    }

    createProductsBaseUrl() {
        return `${this.apiUrl}/products`;
    }

    createProductCategoriesUrl() {
        return `${this.apiUrl}/products/categories`;
    }

    createProductByIdUrl(id) {
        return `${this.apiUrl}/products/${id}`;
    }

    createProductsBulkUpsertUrl() {
        return `${this.apiUrl}/products/bulk-upsert`;
    }

    createProductsUploadImageUrl() {
        return `${this.apiUrl}/products/upload-image`;
    }

    // ------- STORE TYPE --------
    createStoreTypeUrl() {
        return `${this.apiUrl}/storeType`;
    }

    createStoreTypesUrl() {
        return `${this.apiUrl}/storeType/storeTypes`;
    }

    createStoreTypeByValueUrl(value) {
        return `${this.apiUrl}/storeType/${value}`;
    }

    createStoreTypeByIdUrl(id) {
        return `${this.apiUrl}/storeType/id/${id}`;
    }

    createStoreTypeOverridesUrl(value) {
        return `${this.apiUrl}/storeType/overrides/${value}`;
    }

    createStoreTypeOverrideUrl(storeId) {
        return `${this.apiUrl}/storeType/override/store/${storeId}`;
    }

    /** Several stores at once (same segmentation): POST to save, `/remove` to delete. */
    createStoreTypeOverridesBulkUrl(action = '') {
        return `${this.apiUrl}/storeType/override/stores${action ? `/${action}` : ''}`;
    }

    createInteractionSummaryUrl(date) {
        return `${this.apiUrl}/analytics/interactions/summary?date=${date}`;
    }

    createPromoterRouteEfficiencyUrl(userId, date) {
        return `${this.apiUrl}/analytics/promoter-routes?userId=${userId}&date=${date}`;
    }

    createStoreCheckRecencyUrl(startDate, endDate) {
        return `${this.apiUrl}/analytics/storecheck-recency?startDate=${startDate}&endDate=${endDate}`;
    }

    createCompetitorBrandsUrl(category) {
        const base = `${this.apiUrl}/competitor-brands/brands`;
        return category ? `${base}?category=${category}` : base;
    }

    createCompetitorCategoryConfigUrl() {
        return `${this.apiUrl}/competitor-brands/category-config`;
    }

    createSegmentationTypesUrl() {
        return `${this.apiUrl}/segmentation-types`;
    }

    createSegmentationTypeUrl(id) {
        return `${this.apiUrl}/segmentation-types/${id}`;
    }

    createDistributorPriceVisualsUrl(priceType, startDate, endDate, productId) {
        let url = `${this.apiUrl}/distributor/price-visuals?priceType=${encodeURIComponent(priceType)}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
        if (productId) url += `&productId=${encodeURIComponent(productId)}`;
        return url;
    }

    createDistributorPriceAnomalyThresholdUrl() {
        return `${this.apiUrl}/distributor/settings/price-anomaly-threshold`;
    }

    createDistributorPriceObservationReviewUrl() {
        return `${this.apiUrl}/distributor/price-observations/review`;
    }

    // ------- LEVANTAMIENTO GENERAL --------

    createLevantamientoGeneralUrl(params = {}) {
        const q = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return `${this.apiUrl}/levantamiento/general${q ? `?${q}` : ''}`;
    }

    createLevantamientoPriceBySkuUrl(params = {}) {
        const q = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return `${this.apiUrl}/levantamiento/price-by-sku${q ? `?${q}` : ''}`;
    }

    createLevantamientoPriceTrendUrl(params = {}) {
        const q = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return `${this.apiUrl}/levantamiento/price-trend${q ? `?${q}` : ''}`;
    }

    createLevantamientoComparisonUrl(params = {}) {
        const q = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return `${this.apiUrl}/levantamiento/comparison${q ? `?${q}` : ''}`;
    }

    createLevantamientoRebuildUrl() {
        return `${this.apiUrl}/levantamiento/rebuild`;
    }

    createLevantamientoFacetsUrl() {
        return `${this.apiUrl}/levantamiento/facets`;
    }

    createShelfUsageUrl(params = {}) {
        const q = new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
        return `${this.apiUrl}/analytics/shelf-usage${q ? `?${q}` : ''}`;
    }

    _analyticsQuery(params = {}) {
        return new URLSearchParams(
            Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
        ).toString();
    }

    createShareOfShelfUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/share-of-shelf${q ? `?${q}` : ''}`;
    }

    createShareOfShelfStoresUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/share-of-shelf-stores${q ? `?${q}` : ''}`;
    }

    createNumericDistributionUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/numeric-distribution${q ? `?${q}` : ''}`;
    }

    createNumericDistributionStoresUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/numeric-distribution-stores${q ? `?${q}` : ''}`;
    }

    createNumericDistributionStoreProductsUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/numeric-distribution-store-products${q ? `?${q}` : ''}`;
    }

    createProductAvailabilityUrl(params = {}) {
        const q = this._analyticsQuery(params);
        return `${this.apiUrl}/analytics/product-availability${q ? `?${q}` : ''}`;
    }

    createAllProductsUrl() {
        return `${this.apiUrl}/products?all=true`;
    }

    // ------- TRADE --------
    createTradeContractUrl() {
        return `${this.apiUrl}/trade/contract`;
    }

    createAllTradeContractsUrl(includeExpired = true) {
        const params = includeExpired ? '?includeExpired=true' : '';
        return `${this.apiUrl}/trade/contracts${params}`;
    }

    createTradeCampaignUrl() {
        return `${this.apiUrl}/trade/campaign`;
    }

    createAllTradeCampaignsUrl(includeExpired = true) {
        const params = includeExpired ? '?includeExpired=true' : '';
        return `${this.apiUrl}/trade/campaigns${params}`;
    }

    createTradeCampaignByIdUrl(campaignId) {
        return `${this.apiUrl}/trade/campaign/id/${campaignId}`;
    }

    createTradeContractByIdUrl(contractId) {
        return `${this.apiUrl}/trade/contract/id/${contractId}`;
    }

    createUpdateTradeCampaignUrl(campaignId) {
        return `${this.apiUrl}/trade/campaign/update/${campaignId}`;
    }

    createUpdateTradeContractUrl(contractId) {
        return `${this.apiUrl}/trade/contract/update/${contractId}`;
    }

    createTradeContractStorePriceUrl(contractId) {
        return `${this.apiUrl}/trade/contract/${contractId}/store-price`;
    }

    createTradeCampaignStorePriceUrl(campaignId) {
        return `${this.apiUrl}/trade/campaign/${campaignId}/store-price`;
    }

    createContractsByStoreUrl(storeId) {
        return `${this.apiUrl}/trade/contract/${storeId}`;
    }

    createContractsByClientUrl(clientId) {
        return `${this.apiUrl}/trade/contract/client/${clientId}`;
    }

    createCampaignsByStoreUrl(storeId) {
        return `${this.apiUrl}/trade/campaign/store/${storeId}`;
    }

    createCampaignsByClientUrl(clientId) {
        return `${this.apiUrl}/trade/campaign/client/${clientId}`;
    }

    createPromotorAnalyticsUrl(period, limit, channel = 'all', tagId = null, startDate = null, endDate = null) {
        let url = `${this.apiUrl}/analytics/promotors?period=${period}&limit=${limit}&channel=${channel}`;
        if (tagId) url += `&tagId=${tagId}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
        return url;
    }

    createPromotorPerformanceUrl(promotorId, days) {
        return `${this.apiUrl}/analytics/promotors/performance?promotorId=${promotorId}&days=${days}`;
    }

    // ------- POLLS ---------

    createPollUrl() {
        return `${this.apiUrl}/poll`;
    }

    createPollByIdUrl(id) {
        return `${this.apiUrl}/poll/${id}`;
    }

    // ------- TAGS ---------

    createTagsUrl() {
        return `${this.apiUrl}/tags`;
    }

    createTagByIdUrl(tagId) {
        return `${this.apiUrl}/tags/${tagId}`;
    }

    createAssignTagUrl(tagId) {
        return `${this.apiUrl}/tags/${tagId}/assign`;
    }

    createRemoveTagUrl(tagId) {
        return `${this.apiUrl}/tags/${tagId}/remove`;
    }

    createTagItemsUrl(tagId) {
        return `${this.apiUrl}/tags/${tagId}/items`;
    }

    createItemTagsUrl(modelType, modelId) {
        return `${this.apiUrl}/tags/item/${modelType}/${modelId}`;
    }

    createSOSAnalysisUrl(tagId) {
        return `${this.apiUrl}/tags/${tagId}/sos-analysis`;
    }

    // ------- ACTIVITY LOGS ---------

    createActivityLogsUrl() {
        return `${this.apiUrl}/activity-logs`;
    }

    createActivityLogByIdUrl(id) {
        return `${this.apiUrl}/activity-logs/${id}`;
    }

    createActivityLogsStatsUrl() {
        return `${this.apiUrl}/activity-logs/stats`;
    }

    createActivityLogsDeactivationsUrl() {
        return `${this.apiUrl}/activity-logs/deactivations`;
    }

    createActivityLogsByEntityUrl(entityType, entityId) {
        return `${this.apiUrl}/activity-logs/entity/${entityType}/${entityId}`;
    }

    createActivityLogsByUserUrl(userId) {
        return `${this.apiUrl}/activity-logs/user/${userId}`;
    }

    // ------- AUDIT (field options for mobile auditors) -------
    createAuditFieldOptionsUrl() {
        return `${this.apiUrl}/audit/field-options`;
    }

    createAuditAdminAnalyticsUrl() {
        return `${this.apiUrl}/audit/admin/analytics`;
    }

    createAuditAdminAuditProductsUrl(params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/audit-products${q ? `?${q}` : ''}`;
    }

    createAuditAdminAuditProductFacetsUrl() {
        return `${this.apiUrl}/audit/admin/audit-products/facets`;
    }

    createAuditAdminAuditProductByIdUrl(id) {
        return `${this.apiUrl}/audit/admin/audit-products/${id}`;
    }

    createAuditAdminAuditRecordsUrl(params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/audit-records${q ? `?${q}` : ''}`;
    }

    createAuditAdminStoreChecksUrl(params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/store-checks${q ? `?${q}` : ''}`;
    }

    createAuditAdminAuditRecordByIdUrl(id) {
        return `${this.apiUrl}/audit/admin/audit-records/${id}`;
    }

    createAuditAdminStoreCheckRecordsUrl(id) {
        return `${this.apiUrl}/audit/admin/store-checks/${id}/records`;
    }

    createAuditAdminProductDuplicatesUrl(params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/audit-products/duplicates${q ? `?${q}` : ''}`;
    }

    createAuditAdminProductMergeUrl() {
        return `${this.apiUrl}/audit/admin/audit-products/merge`;
    }

    createAuditAdminProductsBulkUrl() {
        return `${this.apiUrl}/audit/admin/audit-products/bulk`;
    }

    createAuditAdminRecordsBulkUrl() {
        return `${this.apiUrl}/audit/admin/audit-records/bulk`;
    }

    createAuditAdminReviewQueueUrl(params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/review-queue${q ? `?${q}` : ''}`;
    }

    createAuditAdminAuditorStatsUrl(userId, params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/auditors/${userId}/stats${q ? `?${q}` : ''}`;
    }

    createAuditAdminAuditorActivityUrl(userId, params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/auditors/${userId}/activity${q ? `?${q}` : ''}`;
    }

    createAuditAdminAuditRecordDeleteUrl(id) {
        return `${this.apiUrl}/audit/admin/audit-records/${id}`;
    }

    createAuditAdminAuditRecordCurrencyUrl(id) {
        return `${this.apiUrl}/audit/admin/audit-records/${id}/currency`;
    }

    createAuditAdminAuditRecordsBulkDeleteUrl() {
        return `${this.apiUrl}/audit/admin/audit-records/bulk-delete`;
    }

    createAuditAdminAuditRecordsBulkCurrencyUrl() {
        return `${this.apiUrl}/audit/admin/audit-records/bulk-currency`;
    }

    createAuditAdminStoreCheckDeleteUrl(id) {
        return `${this.apiUrl}/audit/admin/store-checks/${id}`;
    }

    createAuditAdminAuditProductDeleteUrl(id, params = {}) {
        const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))).toString();
        return `${this.apiUrl}/audit/admin/audit-products/${id}${q ? `?${q}` : ''}`;
    }
}

export default UrlFactory;
